import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { leaveApi } from '../../api/leaveApi';
import {
  LeaveTypeDto,
  LeavePeriodDto,
  LeaveEntitlementDto,
  LeaveRequestDto,
  HolidayDto,
  LeaveUsageReportDto,
  CreateLeaveType,
  UpdateLeaveType,
  CreateLeavePeriod,
  UpdateLeavePeriod,
  CreateLeaveEntitlement,
  BulkCreateLeaveEntitlement,
  UpdateLeaveEntitlement,
  CreateLeaveRequest,
  AssignLeave,
  CreateHoliday,
  UpdateHoliday,
  LeaveEntitlementListResponse,
} from '../../types';

interface LeaveState {
  leaveTypes: LeaveTypeDto[];
  leavePeriods: LeavePeriodDto[];
  entitlements: LeaveEntitlementDto[];
  entitlementTotalCount: number;
  entitlementPageNumber: number;
  entitlementPageSize: number;
  leaveRequests: LeaveRequestDto[];
  holidays: HolidayDto[];
  usageReport: LeaveUsageReportDto[];
  loading: boolean;
  error: string | null;
}

const initialState: LeaveState = {
  leaveTypes: [],
  leavePeriods: [],
  entitlements: [],
  entitlementTotalCount: 0,
  entitlementPageNumber: 1,
  entitlementPageSize: 10,
  leaveRequests: [],
  holidays: [],
  usageReport: [],
  loading: false,
  error: null,
};

// ─── Leave Type Thunks ──────────────────────────────────────

export const fetchLeaveTypes = createAsyncThunk(
  'leave/fetchLeaveTypes',
  async (_, { rejectWithValue }) => {
    try {
      return await leaveApi.getLeaveTypes();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave types');
    }
  }
);

export const createLeaveType = createAsyncThunk(
  'leave/createLeaveType',
  async (data: CreateLeaveType, { rejectWithValue }) => {
    try {
      return await leaveApi.createLeaveType(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create leave type');
    }
  }
);

export const updateLeaveType = createAsyncThunk(
  'leave/updateLeaveType',
  async ({ id, data }: { id: string; data: UpdateLeaveType }, { rejectWithValue }) => {
    try {
      await leaveApi.updateLeaveType(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update leave type');
    }
  }
);

export const deleteLeaveType = createAsyncThunk(
  'leave/deleteLeaveType',
  async (id: string, { rejectWithValue }) => {
    try {
      await leaveApi.deleteLeaveType(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete leave type');
    }
  }
);

// ─── Leave Period Thunks ────────────────────────────────────

export const fetchLeavePeriods = createAsyncThunk(
  'leave/fetchLeavePeriods',
  async (_, { rejectWithValue }) => {
    try {
      return await leaveApi.getLeavePeriods();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave periods');
    }
  }
);

export const createLeavePeriod = createAsyncThunk(
  'leave/createLeavePeriod',
  async (data: CreateLeavePeriod, { rejectWithValue }) => {
    try {
      return await leaveApi.createLeavePeriod(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create leave period');
    }
  }
);

export const updateLeavePeriod = createAsyncThunk(
  'leave/updateLeavePeriod',
  async ({ id, data }: { id: string; data: UpdateLeavePeriod }, { rejectWithValue }) => {
    try {
      await leaveApi.updateLeavePeriod(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update leave period');
    }
  }
);

export const deleteLeavePeriod = createAsyncThunk(
  'leave/deleteLeavePeriod',
  async (id: string, { rejectWithValue }) => {
    try {
      await leaveApi.deleteLeavePeriod(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete leave period');
    }
  }
);

// ─── Leave Entitlement Thunks ───────────────────────────────

export const fetchLeaveEntitlements = createAsyncThunk(
  'leave/fetchEntitlements',
  async (
    params: {
      employeeId?: string;
      leaveTypeId?: string;
      leavePeriodId?: string;
      searchTerm?: string;
      pageNumber?: number;
      pageSize?: number;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      return await leaveApi.getLeaveEntitlements(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch entitlements');
    }
  }
);

export const createLeaveEntitlement = createAsyncThunk(
  'leave/createEntitlement',
  async (data: CreateLeaveEntitlement, { rejectWithValue }) => {
    try {
      return await leaveApi.createLeaveEntitlement(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create entitlement');
    }
  }
);

export const bulkCreateLeaveEntitlement = createAsyncThunk(
  'leave/bulkCreateEntitlement',
  async (data: BulkCreateLeaveEntitlement, { rejectWithValue }) => {
    try {
      return await leaveApi.bulkCreateLeaveEntitlement(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk create entitlements');
    }
  }
);

export const updateLeaveEntitlement = createAsyncThunk(
  'leave/updateEntitlement',
  async ({ id, data }: { id: string; data: UpdateLeaveEntitlement }, { rejectWithValue }) => {
    try {
      await leaveApi.updateLeaveEntitlement(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update entitlement');
    }
  }
);

export const deleteLeaveEntitlement = createAsyncThunk(
  'leave/deleteEntitlement',
  async (id: string, { rejectWithValue }) => {
    try {
      await leaveApi.deleteLeaveEntitlement(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete entitlement');
    }
  }
);

// ─── Leave Request Thunks ───────────────────────────────────

export const fetchLeaveRequests = createAsyncThunk(
  'leave/fetchRequests',
  async (
    params: {
      employeeId?: string;
      leaveTypeId?: string;
      leavePeriodId?: string;
      status?: number;
      fromDate?: string;
      toDate?: string;
      searchTerm?: string;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      return await leaveApi.getLeaveRequests(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave requests');
    }
  }
);

export const createLeaveRequest = createAsyncThunk(
  'leave/createRequest',
  async (data: CreateLeaveRequest, { rejectWithValue }) => {
    try {
      return await leaveApi.createLeaveRequest(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create leave request');
    }
  }
);

export const approveLeaveRequest = createAsyncThunk(
  'leave/approveRequest',
  async ({ id, data }: { id: string; data: { adminRemarks?: string; approvedById: string } }, { rejectWithValue }) => {
    try {
      await leaveApi.approveLeaveRequest(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve leave request');
    }
  }
);

export const rejectLeaveRequest = createAsyncThunk(
  'leave/rejectRequest',
  async ({ id, data }: { id: string; data: { adminRemarks?: string; rejectedById: string } }, { rejectWithValue }) => {
    try {
      await leaveApi.rejectLeaveRequest(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reject leave request');
    }
  }
);

export const cancelLeaveRequest = createAsyncThunk(
  'leave/cancelRequest',
  async (id: string, { rejectWithValue }) => {
    try {
      await leaveApi.cancelLeaveRequest(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel leave request');
    }
  }
);

export const bulkApproveLeaveRequests = createAsyncThunk(
  'leave/bulkApprove',
  async (data: { ids: string[]; approvedById: string; adminRemarks?: string }, { rejectWithValue }) => {
    try {
      return await leaveApi.bulkApproveLeaveRequests(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk approve');
    }
  }
);

export const bulkRejectLeaveRequests = createAsyncThunk(
  'leave/bulkReject',
  async (data: { ids: string[]; rejectedById: string; adminRemarks?: string }, { rejectWithValue }) => {
    try {
      return await leaveApi.bulkRejectLeaveRequests(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk reject');
    }
  }
);

// ─── Assign Leave Thunk ─────────────────────────────────────

export const assignLeave = createAsyncThunk(
  'leave/assignLeave',
  async (data: AssignLeave, { rejectWithValue }) => {
    try {
      return await leaveApi.assignLeave(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to assign leave');
    }
  }
);

// ─── Holiday Thunks ─────────────────────────────────────────

export const fetchHolidays = createAsyncThunk(
  'leave/fetchHolidays',
  async (params: { year?: number; searchTerm?: string } = {}, { rejectWithValue }) => {
    try {
      return await leaveApi.getHolidays(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch holidays');
    }
  }
);

export const createHoliday = createAsyncThunk(
  'leave/createHoliday',
  async (data: CreateHoliday, { rejectWithValue }) => {
    try {
      return await leaveApi.createHoliday(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create holiday');
    }
  }
);

export const updateHoliday = createAsyncThunk(
  'leave/updateHoliday',
  async ({ id, data }: { id: string; data: UpdateHoliday }, { rejectWithValue }) => {
    try {
      await leaveApi.updateHoliday(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update holiday');
    }
  }
);

export const deleteHoliday = createAsyncThunk(
  'leave/deleteHoliday',
  async (id: string, { rejectWithValue }) => {
    try {
      await leaveApi.deleteHoliday(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete holiday');
    }
  }
);

// ─── Leave Usage Report Thunk ───────────────────────────────

export const fetchLeaveUsageReport = createAsyncThunk(
  'leave/fetchUsageReport',
  async (
    params: { leavePeriodId?: string; departmentId?: string; employeeId?: string; searchTerm?: string } = {},
    { rejectWithValue }
  ) => {
    try {
      return await leaveApi.getLeaveUsageReport(params);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch usage report');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────

const leaveSlice = createSlice({
  name: 'leave',
  initialState,
  reducers: {
    clearLeaveError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Leave Types
      .addCase(fetchLeaveTypes.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchLeaveTypes.fulfilled, (state, action: PayloadAction<LeaveTypeDto[]>) => { state.loading = false; state.leaveTypes = action.payload; })
      .addCase(fetchLeaveTypes.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(createLeaveType.pending, (state) => { state.loading = true; })
      .addCase(createLeaveType.fulfilled, (state) => { state.loading = false; })
      .addCase(createLeaveType.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(deleteLeaveType.fulfilled, (state, action) => { state.leaveTypes = state.leaveTypes.filter(lt => lt.id !== action.payload); })
      // Leave Periods
      .addCase(fetchLeavePeriods.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchLeavePeriods.fulfilled, (state, action: PayloadAction<LeavePeriodDto[]>) => { state.loading = false; state.leavePeriods = action.payload; })
      .addCase(fetchLeavePeriods.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(createLeavePeriod.pending, (state) => { state.loading = true; })
      .addCase(createLeavePeriod.fulfilled, (state) => { state.loading = false; })
      .addCase(createLeavePeriod.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(deleteLeavePeriod.fulfilled, (state, action) => { state.leavePeriods = state.leavePeriods.filter(lp => lp.id !== action.payload); })
      // Leave Entitlements
      .addCase(fetchLeaveEntitlements.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchLeaveEntitlements.fulfilled, (state, action: PayloadAction<LeaveEntitlementListResponse>) => {
        state.loading = false;
        state.entitlements = action.payload.items;
        state.entitlementTotalCount = action.payload.totalCount;
        state.entitlementPageNumber = action.payload.pageNumber;
        state.entitlementPageSize = action.payload.pageSize;
      })
      .addCase(fetchLeaveEntitlements.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(deleteLeaveEntitlement.fulfilled, (state, action) => { state.entitlements = state.entitlements.filter(e => e.id !== action.payload); })
      // Leave Requests
      .addCase(fetchLeaveRequests.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchLeaveRequests.fulfilled, (state, action: PayloadAction<LeaveRequestDto[]>) => { state.loading = false; state.leaveRequests = action.payload; })
      .addCase(fetchLeaveRequests.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Holidays
      .addCase(fetchHolidays.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchHolidays.fulfilled, (state, action: PayloadAction<HolidayDto[]>) => { state.loading = false; state.holidays = action.payload; })
      .addCase(fetchHolidays.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(deleteHoliday.fulfilled, (state, action) => { state.holidays = state.holidays.filter(h => h.id !== action.payload); })
      // Usage Report
      .addCase(fetchLeaveUsageReport.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchLeaveUsageReport.fulfilled, (state, action: PayloadAction<LeaveUsageReportDto[]>) => { state.loading = false; state.usageReport = action.payload; })
      .addCase(fetchLeaveUsageReport.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
  },
});

export const { clearLeaveError } = leaveSlice.actions;
export default leaveSlice.reducer;
